import {useFundamentalsLanguage} from '@hconnect/common/hooks'
import {datetimeFormatter} from '@hconnect/uikit'
import {ControlledCommentBox, ControlledCommentProps, spawnErrorToast} from '@hm/fundamental'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {KpiValueEventDto, KpiValueEventType} from '../../types'
import {getSlaCommentValidationSchema} from '../../validators'

import {CommentsVersionsViewItem} from './CommentsVersionsViewItem'
import {VersionPill} from './VersionPill'

type CommentsVersionsViewProps = {
  kpiValueEvents: KpiValueEventDto[]
  canAddComment: boolean
  loggedUserId?: string | null
  onAddComment?: (comment: string) => Promise<void>
  onEditComment?: (commentId: string, commentText: string) => Promise<void>
  onDeleteComment?: (commentId: string) => Promise<void>
}

const COMMENT_MAX_LENGTH = 1000

export const CommentsVersionsView = ({
  kpiValueEvents,
  canAddComment,
  loggedUserId,
  onAddComment,
  onEditComment,
  onDeleteComment
}: CommentsVersionsViewProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const language = useFundamentalsLanguage()
  const [commentInputValue, setCommentInputValue] = useState('')

  const commentEvents = kpiValueEvents.filter((e) => e.eventType === KpiValueEventType.Comment)
  const versionEvents = kpiValueEvents.filter((e) => e.eventType === KpiValueEventType.Version)

  const isOwn = (userId: string | null | undefined) =>
    loggedUserId !== null && loggedUserId !== undefined && userId === loggedUserId

  const comments: ControlledCommentProps<string>[] = commentEvents.map((event) => ({
    id: event.id,
    date: datetimeFormatter(event.eventDate),
    content: event.value,
    name: event.userName ?? 'Unknown',
    ownerId: event.userId ?? undefined,
    isDeletable: isOwn(event.userId),
    isEditable: isOwn(event.userId)
  }))

  const handleAdd = async (comment: string): Promise<void> => {
    if (!canAddComment || !onAddComment) return
    const schema = getSlaCommentValidationSchema({t})
    try {
      await schema.validateAt('comment', {comment})
    } catch {
      spawnErrorToast(t('comments.label.enterComment'))
      return
    }
    if (comment.trim().length > COMMENT_MAX_LENGTH) return
    await onAddComment(comment)
    setCommentInputValue('')
  }

  const handleEdit = async (
    comment: ControlledCommentProps<string>,
    newContent: string
  ): Promise<void> => {
    await onEditComment?.(comment.id, newContent)
  }

  const handleDelete = async (id: string): Promise<void> => {
    await onDeleteComment?.(id)
  }

  return (
    <div className="flex flex-col gap-y-6">
      <ControlledCommentBox
        language={language}
        comments={comments}
        commentInputValue={commentInputValue}
        onCommentInputChange={setCommentInputValue}
        onSubmit={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        disabled={!canAddComment || !onAddComment}
      />
      {versionEvents.map((event) => (
        <CommentsVersionsViewItem
          key={`${event.id}_${event.eventDate}`}
          userName={event.userName ?? 'Unknown'}
          date={datetimeFormatter(event.eventDate)}
          content={<VersionPill version={event.version} value={event.value} />}
        />
      ))}
    </div>
  )
}
